import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subject, Subscription, timer } from 'rxjs';
import { VectorImage as VectorImageLayer } from 'ol/layer.js';
import { Vector as VectorSource } from 'ol/source.js';
import { Fill, Icon, Stroke, Style, Text } from 'ol/style.js';
import { transform } from 'ol/proj.js';
import OlFeature from 'ol/Feature';
import Point from 'ol/geom/Point';

import { ColorData } from '../../shared/others/constant';
import {
  map,
  repeat,
  repeatWhen,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';

import { decode } from '@googlemaps/polyline-codec';
import { encodedPolylines } from './constant/polylines';
import { createTimeTables, getStop } from './demo-utils/demo-timetable';
import moment, { DurationInputArg2 } from 'moment';
import {
  computeTimeInterval,
  findPrevNextStopAfterTime,
  findStopsBeforeTime,
  getNearestTimeSlot,
  getPrevTimeSlot,
  getTimeDifference,
  timeStringAddSeconds,
  timeStringRemoveSeconds,
} from './demo-utils/demo-timer';
import { DemoBusStopTimingService } from './demo-service/bus-stop-timing.service';
import { ActivatedRoute } from '@angular/router';
import { regions } from './constant/regions';
import {
  createStopCodeNameMap,
  findEtaForFirstSequence,
  findEtaFromStopSequence,
  generateAllRouteLadders,
  getNextValidStops,
  getNextValidStopsById,
} from './demo-utils/demo-route-helper';
import { DemoTimingServiceService } from '../demo-service/demo-timing-service.service';
import { Trip } from '../types/mock-timetable-type';
import {
  BusRouteDataMap,
  DirectionStops,
  StreetLines,
} from './constant/route-types';
import { DemoPageFocusService } from '../demo-service/demo-page-focus.service';
import { MockCallServiceService } from '../mock-container/mock-call-wrapper/mock-call-service.service';

// minCoordinate: [103.59, 1.13],
// maxCoordinate: [104.1, 1.49],
function getRandom(min, max) {
  return (
    (Math.random() * (max * 100 - min * 100) + min * 100) /
    100
  ).toPrecision(10);
}

function getAngle(cx, cy, ex, ey) {
  var dy = ey - cy;
  var dx = ex - cx;
  var theta = Math.atan2(dx, dy); // range (-PI, PI]
  theta *= 180 / Math.PI; // rads to degs, range (-180, 180]
  if (theta < 0) theta = 360 + theta; // range [0, 360)
  return theta;
}

@Injectable({
  providedIn: 'root',
})
export class MockMapService {
  busList = [];
  busLayer;
  busListSubscription: Subscription[] = [];
  busSubjects: Subject<void>[] = [];
  mapInstance;
  route;
  routePath;

  isInit;

  busRouteFeatureList;

  activeRoute;

  routePolylines = {};

  startedTrips = [];

  availableBus = [];
  stopData: StreetLines = [];

  constructor(
    private demoBusStopTimingService: DemoBusStopTimingService,
    private demoTimingService: DemoTimingServiceService,
    private demoPageFocusService: DemoPageFocusService,
    private mockCallService: MockCallServiceService
  ) {
    // console.log('polylines: ', this.routePolylines);
  }

  isRegionInit = false;
  region;
  regionData;
  routeData: BusRouteDataMap;
  stopNames;
  busRoutes;
  initRegion(demoId: string) {
    if (this.isRegionInit) return;

    // this.playAudio();

    if (demoId) {
      this.region = demoId;
    } else {
      this.region = 'singapore';
    }
    this.regionData = regions[this.region];
    this.isInit = this.regionData.isInit;
    this.busRouteFeatureList = this.regionData.busRouteFeatureList;
    this.stopData = this.regionData.stopData;
    this.routeData = generateAllRouteLadders(
      this.regionData.routeData,
      this.stopData
    );
    this.regionData.routeData = this.routeData;
    this.stopNames = createStopCodeNameMap(this.stopData);

    this.regionData.stopNames = this.stopNames;
    this.busRoutes = this.regionData.busRoutes;
    this.availableBus = this.regionData.busList;

    // this.demoBusStopTimingService.initService(this.regionData);
    this.demoTimingService.initTimetables(this.regionData);

    const decodedPolylines = Object.entries(this.regionData.polylines).reduce(
      (prev, [route, polylineArr]: [string, any]) => {
        const routePolylines = [];
        polylineArr.forEach(polylineObj => {
          const { value, path } = polylineObj;
          const decodedPolyline = decode(path as string);
          routePolylines.push({
            value,
            path: decodedPolyline,
          });
        });
        prev[route] = routePolylines;
        return prev;
      },
      {}
    );
    this.routePolylines = decodedPolylines;
    // initialize bus routes to start
    // Object.entries(this.routeData)().forEach(
    //   ([route, routeData]: [string, any]) => {
    //     this.initBus(route, routeData, routeData.busCount, true);
    //   }
    // );

    this.startService();
    this.isRegionInit = true;
    this.detectFocus();
  }

  setActiveRoute(route) {
    this.activeRoute = route;
  }

  initBusLayer(mapInstance) {
    this.mapInstance = mapInstance;
    const layerName = 'bus-layer';
    const vectorLayer = new VectorImageLayer({
      source: new VectorSource({
        features: [],
        useSpatialIndex: false,
      }),
      renderBuffer: 16,
      className: layerName,
      properties: {
        name: layerName,
      },
    });

    vectorLayer.setZIndex(2);
    vectorLayer.setVisible(true);

    this.mapInstance.addLayer(vectorLayer);
    this.busLayer = vectorLayer;
  }

  initService = {};
  // availableTrips: Trip[] = [];
  startService(routeId?: string) {
    Object.entries(this.routeData).forEach(([routeId, routeData]) => {
      if (!this.initService[routeId]) {
        const { busArr, busIdStr, stops } = routeData;
        // this.availableTrips =
        //   this.demoTimingService.getAvailableTripsAndRecordPastTrips(routeId);
        const filteredTrips = this.demoTimingService.availableTrips.filter(
          trip => trip.routeId === routeId
        );
        this.startAvailableTrips(filteredTrips, busArr, routeId, true);
        // console.log({ routeId, availableTrips: this.availableTrips });
        this.initService[routeId] = true;
      }
    });
    // if service is already initiated
    // just add each bus to bus layer
    if (routeId && this.initService[routeId]) {
      this.busRouteFeatureList[routeId].forEach(bus => {
        this.busLayer.getSource().addFeature(bus);
      });
    }
    console.log('Start Service: ', this.busRouteFeatureList);
  }

  startAvailableTrips(
    trips: Trip[],
    busArr: string[],
    routeId: string,
    startup = false
  ) {
    const newTimeCurrent = moment().format('HH:mm:ss');

    busArr.forEach(busId => {
      const filteredTrips = trips.filter(
        trip => trip.vehicleId === busId && trip.routeId === routeId
      );
      console.log('Trips: ', { busId, filteredTrips });

      filteredTrips.forEach((assignedTrip, tripIndex) => {
        // fill up previous stops
        const startedStops = findStopsBeforeTime(assignedTrip, newTimeCurrent);
        if (startedStops?.length > 0) {
          assignedTrip.isStarted = true;
        }
        startedStops.forEach(stop => {
          const { schArrTime } = stop;
          stop.schArrTime = timeStringRemoveSeconds(schArrTime);
          const randomSeconds = Math.floor(Math.random() * 61);
          stop.obsArrTime = timeStringAddSeconds(schArrTime, randomSeconds);
          stop.busRegNo = busId;
          stop.actualHeadway = '-';
          stop.planHeadway = '-';
          stop.headwayStatusColor = 'bgGray';
          stop.isAdded = true;
          stop.statusColor = 'on-time';
        });
      });

      // initiate moving
      const firstTrip = filteredTrips?.[0];
      if (firstTrip) {
        this.moveTrip(firstTrip, startup);
      }
    });
  }

  moveTrip(trip: Trip, startup: boolean) {
    const {
      direction,
      stops: stopsTimetable,
      vehicleId,
      routeId,
      tripId,
      blockId,
    } = trip;
    let assignedTrip = trip;

    const routePath = this.routePolylines[routeId];
    let currentRoute = this.routeData[routeId];

    // bus stops
    let stopsForPathIndex: DirectionStops[] = [];
    const { stops: tempStops } = currentRoute;
    stopsForPathIndex = tempStops;
    let directionNumber = +direction;

    // currStop
    let currentPathStops = stopsForPathIndex.find(
      stop => stop.direction === directionNumber
    );

    // currPath
    let currentPath = routePath.find(dir => dir.value === directionNumber)
      ?.path;

    const newTimeCurrent = moment().format('HH:mm:ss');

    const { nextStop, prevStop } = findPrevNextStopAfterTime(
      trip,
      newTimeCurrent
    );
    let prevTimeSlot = prevStop.schArrTime;
    let nextTimeSlot = nextStop.schArrTime;
    // console.log(vehicleId, routeId, { prevStop, nextStop });

    // console.log(vehicleId, 'buslist', this.busList);
    // check if bus is already added to simulation
    if (
      this.busList.findIndex(busItem => busItem?.data?.busCode === vehicleId) <
      0
    ) {
      // pathStop
      trip.isStarted = true;
      let currentDirPathStops = currentPathStops.stop;
      let prevStopSequence =
        currentDirPathStops.findIndex(
          stopSequence => stopSequence.stopCode === prevStop?.stopId
        ) ?? 0;
      const prevPathStopObj =
        currentDirPathStops?.[prevStopSequence] ?? undefined;
      const prevPathStopIndex = prevPathStopObj?.index ?? 0;
      const nextPathStopIndex =
        currentDirPathStops.find(
          stopSequence => stopSequence.stopCode === nextStop?.stopId
        )?.index ?? 0;

      const totalTimeDiff = getTimeDifference(prevTimeSlot, nextTimeSlot);
      const prevTimeDiff = getTimeDifference(prevTimeSlot, newTimeCurrent);

      const nextTimeDiff = Math.min(
        getTimeDifference(newTimeCurrent, nextTimeSlot),
        totalTimeDiff
      );

      const isLastStop = +prevStop?.stopSequence === currentDirPathStops.length;
      const isFirstStop = prevStop?.schArrTime === nextStop.schArrTime;

      if (isFirstStop) {
        // console.log('check stop: ', vehicleId, routeId, {
        //   assignedTrip,
        //   prevStop,
        //   nextStop,
        // });
      }

      const timePercent = 1 - nextTimeDiff / totalTimeDiff;

      if (!isLastStop && !isFirstStop && prevStop && prevTimeDiff > 0) {
        // ??
      }

      const prevIndex = isFirstStop
        ? 0
        : prevPathStopIndex +
          Math.floor((nextPathStopIndex - prevPathStopIndex) * timePercent);

      let sequenceIndex = isLastStop ? 0 : prevStopSequence;
      let pathIndex = isFirstStop || isLastStop ? 0 : prevIndex;

      // TODO: add handler for multiple directions
      if (isLastStop) {
        currentPath = routePath.find(dir => dir.value === directionNumber)
          ?.path;
        currentPathStops = stopsForPathIndex.find(
          stop => stop.direction === directionNumber
        );
        currentDirPathStops = currentPathStops.stop;
      }

      let rotation = 0;
      const coordItem = currentPath[pathIndex];
      let [latitude, longitude] = coordItem;
      if (pathIndex < currentPath.length) {
        const newCoords = currentPath[pathIndex];
        const [newLat, newLong] = newCoords;
        rotation = getAngle(+longitude, +latitude, +newLong, +newLat);
      }

      const notOnTrip = prevTimeDiff < 0;
      const busData = {
        status: notOnTrip ? '2' : '0',
        lon: longitude,
        lat: latitude,
        degrees: rotation,
        route: routeId,
        trip: tripId,
        busCode: vehicleId,
        description: `${vehicleId}-bus`,
        busFeature: null,
        blockId,
        tripId,

        // stop info
        stops: [
          { stop: '', eta: '-' },
          { stop: '', eta: '-' },
          { stop: '', eta: '-' },
        ],

        // bus ladder info
        prevStopId: '',
        nextStopId: '',
        drivenPercentage: 0,
        direction: `${directionNumber}`,
      };

      // add trip id to list
      this.startedTrips.push(tripId);

      const newBus = this.addBus(busData);

      if (!startup) {
        this.busLayer.getSource().addFeature(newBus);
      }
      this.busRouteFeatureList[routeId].push(newBus);

      const prevBusStop = currentDirPathStops[sequenceIndex].index;
      const nextBusStop =
        sequenceIndex >= currentDirPathStops.length - 1
          ? currentPath.length
          : currentDirPathStops[sequenceIndex + 1].index;

      const initPrevStop = currentDirPathStops[sequenceIndex] ?? undefined;
      const initFirstStop =
        currentDirPathStops?.[sequenceIndex + 1] ?? undefined;

      this.updateDemoBusLadder(
        busData,
        initPrevStop?.stopCode,
        initFirstStop?.stopCode,
        timePercent * 100
      );

      if (!notOnTrip || isFirstStop) {
        const currBusValue = currentDirPathStops?.[sequenceIndex];
        const { stops } = trip;
        let nextStopsEta = [undefined, undefined, undefined];
        if (isFirstStop) {
          nextStopsEta = getNextValidStops(stops, -1, 3);
        } else {
          nextStopsEta = getNextValidStops(stops, sequenceIndex, 3);
        }

        // const initFirstStop =
        //   currentDirPathStops?.[sequenceIndex + 1] ?? undefined;
        // const initSecondStop =
        //   currentDirPathStops?.[sequenceIndex + 2] ?? undefined;
        // const initThirdStop =
        //   currentDirPathStops?.[sequenceIndex + 3] ?? undefined;
        const initStopsEta = [
          nextStopsEta[0],
          nextStopsEta[1],
          nextStopsEta[2],
        ];

        busData.stops = busData.stops.map((_, index) => {
          const currStopEtaItem = initStopsEta?.[index];
          let timeDiff = 0;
          if (currStopEtaItem) {
            const { stopSequence } = currStopEtaItem || {};
            timeDiff = findEtaFromStopSequence(stopSequence, stopsTimetable);
          }

          return this.updateBusStopEta(
            initStopsEta[index],
            timeDiff,
            'millisecond'
          );
        });
      }

      const firstTimeDiff = findEtaForFirstSequence(stopsTimetable);

      let isTest = false;
      const testInterval = 100;
      let interval = isTest
        ? testInterval
        : isFirstStop
          ? computeTimeInterval(prevBusStop, nextBusStop, firstTimeDiff)
          : computeTimeInterval(prevIndex, nextBusStop, nextTimeDiff);

      const indexSubject = new BehaviorSubject(interval);
      let delaySub = isTest ? 0 : prevTimeDiff < 0 ? prevTimeDiff * -1 : 0;
      const _stop = new Subject<void>();
      const _start = new Subject<void>();
      // create subscription to move buses
      function start(): void {
        _start.next();
      }

      function stop(): void {
        _stop.next();
      }

      function changeInterval(newInterval: number) {
        delaySub = 0;
        interval = newInterval;
        if (isTest) {
          indexSubject.next(testInterval);
        } else {
          indexSubject.next(interval);
        }
      }
      const busObservable = indexSubject.pipe(
        switchMap(duration => timer(delaySub, duration)),
        map(x => x + pathIndex + 1),
        takeUntil(_stop),
        repeatWhen(() => _start)
      );

      let prevStopPathIndex = prevBusStop ?? 1;
      let nextStopPathIndex = nextBusStop;
      const initPosition = this.computePercent(
        prevStopPathIndex,
        nextStopPathIndex,
        pathIndex
      );
      busData.drivenPercentage = initPosition;

      let currTripIndex = 0;

      const busSubscription = busObservable.subscribe(x => {
        if (x <= currentPath.length) {
          const busStopIndex = currentPathStops.stop.findIndex(
            stop => stop.index === x
          );

          /**
           * call timetable update here
           */
          if (busStopIndex > -1) {
            const currTime = moment().format('HH:mm:ss');
            const currBusStop = currentPathStops.stop[busStopIndex];

            const { stops } = assignedTrip;
            const { stopCode } = currBusStop;
            const nextStopsEta = getNextValidStopsById(stops, stopCode, 3);

            const nextBusStop =
              currentPathStops.stop[busStopIndex + 1] ?? undefined;
            // const secondBusStop =
            //   currentPathStops.stop?.[busStopIndex + 2] ?? undefined;
            // const thirdBusStop =
            //   currentPathStops.stop?.[busStopIndex + 3] ?? undefined;

            // update busladder
            // removes bus on ladder
            this.updateDemoBusLadder(
              busData,
              currBusStop?.stopCode,
              nextBusStop?.stopCode,
              0
            );

            const stopsEtaData = [
              nextStopsEta[0],
              nextStopsEta[1],
              nextStopsEta[2],
            ];
            const { index } = currBusStop;

            prevStopPathIndex = index ?? 1;
            nextStopPathIndex = nextBusStop?.index;

            const { tripId, routeId, vehicleId, direction } = assignedTrip;

            const currentTripStop = stops.find(
              stop => stop.stopId === stopCode
            );
            const { skip = false } = currentTripStop;

            // console.log(vehicleId, 1, 'Bus Stop', {
            //   x,
            //   busData,
            //   currBusStop,
            //   nextBusStop,
            //   currTime,
            //   index,
            // });

            // update stops
            busData.stops = busData.stops.map((_, index) => {
              const currStopEtaItem = stopsEtaData?.[index];
              let timeDiff = 0;
              if (currStopEtaItem) {
                const { stopSequence } = currStopEtaItem || {};
                timeDiff = findEtaFromStopSequence(
                  stopSequence,
                  stopsTimetable
                );
              }
              return this.updateBusStopEta(
                stopsEtaData[index],
                timeDiff,
                'millisecond'
              );
            });

            if (skip) {
            } else {
              // console.log('timetable arrival 1 ', routeId, tripId, stopCode);
              this.demoTimingService.timetableArrival({
                routeId,
                direction: direction,
                tripId,
                stopId: stopCode,
                obsArrTime: currTime,
                busRegNo: vehicleId,
              });
            }

            // update interval
            if (nextBusStop) {
              pathIndex = index;
              const { stopSequence: newStopSequence } = nextBusStop;
              const newTimeDiff = findEtaFromStopSequence(
                +newStopSequence,
                stopsTimetable
              );
              const newInterval = computeTimeInterval(
                index,
                nextBusStop.index,
                newTimeDiff
              );
              // console.log('new interval', newInterval);
              changeInterval(newInterval);
            }
          }

          // move bus
          if (x < currentPath.length) {
            const newCoordinates = currentPath[x];
            const [newLat, newLong] = newCoordinates;

            newBus
              .getGeometry()
              .setCoordinates(
                transform(
                  [Number(newLong), Number(newLat)],
                  'EPSG:4326',
                  'EPSG:3857'
                )
              );

            const newRotation = getAngle(
              +longitude,
              +latitude,
              +newLong,
              +newLat
            );

            busData.lat = newLat;
            busData.lon = newLong;
            busData.degrees = newRotation;

            // update bus ladder position
            const newDrivenPercentage = this.computePercent(
              prevStopPathIndex,
              nextStopPathIndex,
              x
            );
            // console.log('zpercent', busCodePlate, {
            //   prevStopPathIndex,
            //   nextStopPathIndex,
            //   x,
            //   newDrivenPercentage,
            // });
            busData.drivenPercentage = newDrivenPercentage;

            // update early / late here
            busData.status = '0';

            const newBusStyle = this.createNewBusStyle(busData);
            newBus.setStyle(newBusStyle);

            latitude = newLat;
            longitude = newLong;
          }
        }

        if (x >= currentPath.length) {
          pathIndex = 0;
          stop();

          currTripIndex = currTripIndex + 1;

          console.log(
            'finished trip',
            vehicleId,
            tripId,
            busData,
            assignedTrip
          );

          // clear all stops ETA
          busData.stops.forEach(stop => {
            stop.stop = '';
            stop.eta = '-';
          });
          // busData.trip = '';
          // busData.tripId = '';

          // clear bus stop ladder
          // this.updateDemoBusLadder(busData, undefined, undefined, 0);

          busData.status = '2';
          const newBusStyle = this.createNewBusStyle(busData);
          newBus.setStyle(newBusStyle);
          const busTrips = this.demoTimingService.availableTrips.filter(
            trip => trip.vehicleId === vehicleId
          );
          if (currTripIndex < this.demoTimingService.availableTrips.length) {
            // console.log('next trips: ', vehicleId, {
            //   busTrips,
            //   trips: this.demoTimingService.availableTrips,
            // });
            assignedTrip = busTrips.find(trip => trip.isStarted != true);
            // console.log(
            //   'assigned trip',
            //   routeId,
            //   assignedTrip,
            //   JSON.parse(JSON.stringify(busTrips))
            // );

            if (assignedTrip) {
              const { blockId, tripId, vehicleId, routeId, direction } =
                assignedTrip;
              directionNumber = +direction;
              currentPath = routePath.find(dir => dir.value === directionNumber)
                ?.path;

              currentPathStops = stopsForPathIndex?.find(
                stop => stop.direction === directionNumber
              );
              const prevStop = currentPathStops.stop[0];
              const nextStop = currentPathStops.stop[1];

              directionNumber = +direction;
              busData.status = '2';

              assignedTrip.isStarted = true;

              const currTime = moment().format('HH:mm:ss');
              const nextTime = assignedTrip.stops[0].schArrTime;

              const waitTime = getTimeDifference(currTime, nextTime);

              setTimeout(
                () => {
                  busData.trip = tripId;
                  busData.tripId = tripId;
                  busData.blockId = blockId;
                  busData.busCode = vehicleId;

                  busData.direction = `${directionNumber}`;

                  this.updateDemoBusLadder(
                    busData,
                    prevStop.stopCode,
                    prevStop.stopCode,
                    0
                  );

                  const newTimeDiff = findEtaForFirstSequence(stopsTimetable);
                  const newInterval = computeTimeInterval(
                    0,
                    nextStop.index,
                    newTimeDiff
                  );
                  changeInterval(newInterval);
                  start();

                  currentRoute = this.routeData[routeId];
                  const { stops: newStops } = currentRoute;
                  stopsForPathIndex = newStops;
                  currentPathStops = stopsForPathIndex.find(
                    stop => stop.direction === directionNumber
                  );

                  const currBusStop = currentPathStops.stop[0];
                  const { stopCode } = currBusStop;

                  // console.log(
                  //   'timetable arrival 2 ',
                  //   routeId,
                  //   tripId,
                  //   stopCode
                  // );
                  this.demoTimingService.timetableArrival({
                    routeId: routeId,
                    direction: directionNumber,
                    tripId,
                    stopId: stopCode,
                    obsArrTime: currTime,
                    busRegNo: vehicleId,
                  });

                  // set eta stops
                  const { stops } = assignedTrip;
                  const nextStopsEta = getNextValidStopsById(
                    stops,
                    stopCode,
                    3
                  );
                  const nextBusStop = currentPathStops.stop[1] ?? undefined;
                  // const secondBusStop = currentPathStops.stop?.[2] ?? undefined;
                  // const thirdBusStop = currentPathStops.stop?.[3] ?? undefined;

                  const stopsEtaData = [
                    nextStopsEta[0],
                    nextStopsEta[1],
                    nextStopsEta[2],
                  ];
                  busData.stops = busData.stops.map((_, index) => {
                    const currStopEtaItem = stopsEtaData?.[index];
                    let timeDiff = 0;
                    if (currStopEtaItem) {
                      const { stopSequence } = currStopEtaItem || {};
                      timeDiff = findEtaFromStopSequence(
                        +stopSequence,
                        stopsTimetable
                      );
                    }
                    return this.updateBusStopEta(
                      stopsEtaData[index],
                      timeDiff,
                      'millisecond'
                    );
                  });

                  // set new busladder
                  this.updateDemoBusLadder(
                    busData,
                    currBusStop.stopCode,
                    nextBusStop.stopCode,
                    0
                  );

                  prevStopPathIndex = 1;
                  nextStopPathIndex = nextBusStop.index;
                },
                isTest ? 5000 : waitTime
              );
            } else {
              console.log('finished all trips', vehicleId);
              this.busListSubscription = this.busListSubscription.filter(
                busSub => busSub !== busSubscription
              );
              this.busSubjects = this.busSubjects.filter(
                busSubj => busSubj !== _start || busSubj !== _stop
              );
              busSubscription.unsubscribe();
              this.busList = this.busList.filter(
                bus => bus?.data?.busCode !== vehicleId
              );
            }
          }
        }
      });

      this.busListSubscription.push(busSubscription);
      this.busSubjects.push(_start);
      this.busSubjects.push(_stop);

      this.busList.push({ data: busData, busFeature: newBus });
    } else {
      const currBus = this.busList.find(
        busItem => busItem?.data?.busCode === vehicleId
      );
      const { busFeature } = currBus;
      this.busLayer.getSource().addFeature(busFeature);
    }
  }

  assignTrip(trip: Trip) {
    // console.log('Available Trips: ', this.availableTrips);
    this.moveTrip(trip, true);
  }

  isFocused = true;
  isDev = true;
  detectFocus() {
    this.demoPageFocusService.focusChanges$.subscribe(focused => {
      if (!this.isFocused && focused) {
        console.log('Focus Test: ', { old: this.isFocused, new: focused });
        if (!this.isDev) {
          window.location.reload();
        }

        this.reInitializeService();
      }
      this.isFocused = focused;
    });
  }

  reInitializeService() {
    // this.busListSubscription.forEach(busSub => busSub?.unsubscribe());
    // this.busSubjects.forEach(busSubj => busSubj.complete());
    // this.busListSubscription = [];
    // this.busSubjects = [];
    // this.clearBus();
    // this.clearService();
    // this.busList = [];
    // this.demoTimingService.clearRoutes();
    // this.demoTimingService.initTimetables(this.regionData);
    // this.startService();
  }

  clearService() {
    this.initService = Object.entries(this.initService).reduce(
      (prev, [key, value]) => {
        prev[key] = false;
        return prev;
      },
      {}
    );
  }

  // starts specific bus
  initBus(route, routeData, numBus = 3, startup = false) {
    this.route = route;
    const {
      busCode,
      busArr,
      busId,
      busIdStr,
      stops,
      timeStop = 5,
      timeslotPeriodMinutes = 15,
      isFixed = false,
    } = routeData;
    const newTimeCurrent = moment();
    const newTimeCurrentFormatted = newTimeCurrent.format('HH:mm:ss');
    const newTimeslot = getNearestTimeSlot(
      newTimeCurrent,
      timeslotPeriodMinutes
    ); // scheduled trip starts
    // console.log('initbus', routeData);
    this.routePath = this.routePolylines[busId];
    const routePath = this.routePath;
    const currRoute = this.route;

    if (!this.isInit[route]) {
      this.isInit[route] = true;
      let assignedTrips = [];
      // if (isFixed) {
      //   assignedTrips =
      //     this.demoBusStopTimingService.getBlockTripsByCurrenttime(route);
      // } else {
      //   assignedTrips = this.demoBusStopTimingService.getBlockTrips(
      //     route,
      //     15,
      //     5,
      //     true
      //   );
      // }

      assignedTrips = this.demoBusStopTimingService.getBlockTrips(
        route,
        15,
        5,
        true
      );

      console.log(busIdStr, 'init trip schedule', {
        newTimeslot,
        timetable: this.demoBusStopTimingService.timetable,
        assignedTrips,
        // nearestTrip,
      });

      for (let i = 0; i < assignedTrips.length; i++) {
        // check if assigned trip is undefined
        let assignedTrip = assignedTrips[i];
        if (!assignedTrip) continue;

        const busPlate = busArr[i];

        // const busCodePlate = `${busCode}-${busPlate}`;
        // parse currentTrip
        const currTime = moment();
        let prevTimeSlot = getPrevTimeSlot(currTime, timeStop);
        let currTimeSlot = getNearestTimeSlot(currTime, timeStop);
        let nextTimeSlot = getNearestTimeSlot(currTime, timeStop);

        const { blockId } = assignedTrip;
        const busCodePlate = blockId;

        let prevStop =
          getStop(assignedTrip.stops, prevTimeSlot) ?? assignedTrip.stops[0];
        let nextStop =
          getStop(assignedTrip.stops, nextTimeSlot) ?? assignedTrip.stops[1];

        prevTimeSlot = prevStop.schArrTime;
        nextTimeSlot = nextStop.schArrTime;

        // get timetable for blockId
        const blockTimeTableData =
          this.demoBusStopTimingService.getTimeTableByBlock(
            route,
            assignedTrip.blockId,
            assignedTrip.tripId
          );

        const { timeTableData: blockTimeTable, currentTripData } =
          blockTimeTableData;

        // timetable fill up data
        const { tripId: tempTripId } = assignedTrip;
        const { flatTimeTable: tempFlatTable } = blockTimeTable;

        const filteredFlatTable = tempFlatTable.filter(
          ttable => ttable.blockId === busCodePlate
        );

        const tempTripIndex = filteredFlatTable.findIndex(
          trip => trip.tripId === tempTripId
        );

        if (tempTripIndex > -1) {
          const slicedTrips = tempFlatTable.slice(0, tempTripIndex);
          // console.log(
          //   'zxc',
          //   busCodePlate,
          //   tempFlatTable,
          //   slicedTrips,
          //   assignedTrip,
          //   tempTripIndex
          // );

          slicedTrips.forEach(trip => {
            const { stops, tripId, direction } = trip;
            stops.forEach(stop => {
              const { schArrTime, stopId } = stop;
              const plusOrMinus = Math.random() < 0.5 ? -1 : 1;
              const addOrNot = Math.random() < 0.5 ? 0 : 1;
              this.demoBusStopTimingService.timetableArrival({
                routeId: route,
                direction: direction,
                tripId: tripId,
                stopId: stopId,
                obsArrTime: moment(schArrTime, 'HH:mm:ss')
                  .add(plusOrMinus * addOrNot, 'minute')
                  .format('HH:mm'),
                busRegNo: busCodePlate,
              });
            });
          });
        }

        let currTripHelpData = currentTripData;
        let { direction: currTripDirection, index: currTripIndex } =
          currentTripData;

        // let rotation = getRandom(0, 360);
        let rotation = 0;
        // const longitude = getRandom(103.59, 104.1);
        // const latitude = getRandom(1.13, 1.49);
        let currDir = currTripDirection
          ? +currTripDirection
          : prevStop?.direction
            ? +prevStop.direction
            : 1;
        let currPath = routePath.find(dir => dir.value === currDir)?.path;

        // console.log(
        //   'trip: ',
        //   busCodePlate,
        //   i,
        //   currTime.format('HH:mm:ss'),
        //   assignedTrip,
        //   prevStop,
        //   nextStop,
        //   currDir
        // );

        // console.log('trip timeslots: ', busCodePlate, {
        //   prevTimeSlot,
        //   currTimeSlot,
        //   nextTimeSlot,
        // });

        const busStops = stops;
        // console.log('bus stops', stops);
        let currStop = busStops.find(stop => stop.direction === currDir);

        if (
          this.busList.findIndex(
            busItem => busItem?.data?.busCode === busCodePlate
          ) < 0
        ) {
          // const currStopSequence = stops.find(
          //   stop => stop.direction === currDir
          // ).stop;

          let pathStop = currStop.stop;

          // findPrev pathIndex and next pathIndex
          let prevStopSequence =
            pathStop.findIndex(
              stopSequence => stopSequence.stopCode === prevStop?.stopId
            ) ?? 0;

          // console.log(busCodePlate, 'path stop', pathStop, prevStopSequence);
          const prevStopObj = pathStop?.[prevStopSequence] ?? undefined;
          const prevStopIndex = prevStopObj?.index ?? 0;

          const nextStopIndex =
            pathStop.find(
              stopSequence => stopSequence.stopCode === nextStop?.stopId
            )?.index ?? 0;

          const totalTimeDiff = getTimeDifference(prevTimeSlot, nextTimeSlot);
          const prevTimeDiff = getTimeDifference(
            prevTimeSlot,
            currTime.format('HH:mm:ss')
          );

          const nextTimeDiff = Math.min(
            getTimeDifference(currTime.format('HH:mm:ss'), nextTimeSlot),
            totalTimeDiff
          );

          const isLastStop = +prevStop?.stopSequence === pathStop.length;
          const isFirstStop = prevStop?.schArrTime === nextStop.schArrTime;

          const timePercent = 1 - nextTimeDiff / totalTimeDiff;

          // record previous stops arrival in timetable
          // console.log(busCodePlate, 'current trip', {
          //   prevStopObj,
          //   assignedTrip,
          //   prevStop,
          //   isLastStop,
          //   isFirstStop,
          //   prevTimeDiff,
          // });

          // fill up prev stop
          if (!isLastStop && !isFirstStop && prevStop && prevTimeDiff > 0) {
            const { stopSequence: endTimetableStopSequence } = prevStop;
            const { stops: timetableStops } = assignedTrip;
            for (let i = 0; i < endTimetableStopSequence; i++) {
              const currTimeTableStop = timetableStops[i];
              if (currTimeTableStop) {
                this.demoBusStopTimingService.timetableArrival({
                  routeId: route,
                  direction: currDir,
                  tripId: assignedTrip.tripId,
                  stopId: currTimeTableStop.stopId,
                  obsArrTime: moment(
                    currTimeTableStop.schArrTime,
                    'HH:mm:ss'
                  ).format('HH:mm'),
                  busRegNo: busCodePlate,
                });
              }
            }
          }

          // console.log(
          //   busCodePlate,
          //   'time diff',
          //   prevTimeDiff,
          //   nextTimeDiff,
          //   totalTimeDiff
          // );

          // console.log('time index', busCodePlate, {
          //   prevStopIndex,
          //   nextStopIndex,
          //   timePercent,
          // });

          const prevIndex = isFirstStop
            ? 0
            : prevStopIndex +
              Math.floor((nextStopIndex - prevStopIndex) * timePercent);

          let sequenceIndex = isLastStop ? 0 : prevStopSequence;
          let pathIndex = isFirstStop || isLastStop ? 0 : prevIndex;

          // if last stop need to get next trip and reverse direction
          if (isLastStop) {
            currDir = currDir === 1 ? 2 : 1;
            currPath = routePath.find(dir => dir.value === currDir)?.path;
            currStop = busStops.find(stop => stop.direction === currDir);
            pathStop = currStop.stop;
          }

          // console.log(
          //   'trip stop obj: ',
          //   busCodePlate,
          //   currStop,
          //   // stopObj,
          //   prevStopIndex,
          //   prevIndex,
          //   timePercent,
          //   nextStopIndex,
          //   isFirstStop,
          //   isLastStop,
          //   pathIndex
          // );

          // console.log(
          //   'coord check',
          //   busCodePlate,
          //   currPath,
          //   pathIndex,
          //   routePath
          // );

          const coordItem = currPath[pathIndex];
          let [latitude, longitude] = coordItem;
          if (pathIndex < currPath.length) {
            const newCoords = currPath[pathIndex];
            const [newLat, newLong] = newCoords;
            rotation = getAngle(+longitude, +latitude, +newLong, +newLat);
            // console.log(
            //   'rotation: ',
            //   rotation,
            //   +longitude,
            //   +latitude,
            //   +newLong,
            //   +newLat
            // );
          }

          const notOnTrip = prevTimeDiff < 0;
          const { blockId, tripId } = assignedTrip;
          const busData = {
            status: notOnTrip ? '2' : '0',
            lon: longitude,
            lat: latitude,
            degrees: rotation,
            route,
            trip: tripId,
            busCode: busCodePlate,
            description: `${busCode}-bus-${i + 1}`,
            busFeature: null,
            blockId,
            tripId,

            // stop info
            stops: [
              { stop: '', eta: '-' },
              { stop: '', eta: '-' },
              { stop: '', eta: '-' },
            ],

            // bus ladder info
            prevStopId: '',
            nextStopId: '',
            drivenPercentage: 0,
            direction: `${currDir}`,
          };

          // add trip id to list
          this.startedTrips.push(tripId);

          const newBus = this.addBus(busData);

          if (!startup) {
            this.busLayer.getSource().addFeature(newBus);
          }

          this.busRouteFeatureList[route].push(newBus);

          const prevBusStop = pathStop[sequenceIndex].index;
          const nextBusStop =
            sequenceIndex >= pathStop.length - 1
              ? currPath.length
              : pathStop[sequenceIndex + 1].index;

          // setup initial stop data
          // console.log(
          //   busCodePlate,
          //   'next bus stop init',
          //   { nextTimeDiff },
          //   pathStop
          // );
          const initPrevStop = pathStop[sequenceIndex] ?? undefined;
          const initFirstStop = pathStop?.[sequenceIndex + 1] ?? undefined;
          // console.log(
          //   'init bus ladder',
          //   busCodePlate,
          //   busData,
          //   initPrevStop?.stopCode,
          //   initFirstStop?.stopCode,
          //   timePercent * 100
          // );

          this.updateDemoBusLadder(
            busData,
            initPrevStop?.stopCode,
            initFirstStop?.stopCode,
            timePercent * 100
          );

          if (!notOnTrip) {
            const initFirstStop = pathStop?.[sequenceIndex + 1] ?? undefined;
            const initSecondStop = pathStop?.[sequenceIndex + 2] ?? undefined;
            const initThirdStop = pathStop?.[sequenceIndex + 3] ?? undefined;
            const initStopsEta = [initFirstStop, initSecondStop, initThirdStop];

            busData.stops = busData.stops.map((_, index) =>
              this.updateBusStopEta(
                initStopsEta[index],
                nextTimeDiff + 300000 * index,
                'millisecond'
              )
            );
          }

          let isTest = false;
          const testInterval = 10;
          // let interval = computeTimeInterval(prevBusStop, nextBusStop);
          // const initGoalTime = 300000; // 5 minutes in MS = 300000, 30s = 30000;
          let interval = isTest
            ? testInterval
            : isFirstStop
              ? computeTimeInterval(prevBusStop, nextBusStop)
              : computeTimeInterval(prevIndex, nextBusStop, nextTimeDiff);

          // console.log(busCodePlate, 'start interval', interval);
          const indexSubject = new BehaviorSubject(interval);

          let delaySub = isTest ? 0 : prevTimeDiff < 0 ? prevTimeDiff * -1 : 0;

          const _stop = new Subject<void>();
          const _start = new Subject<void>();
          // create subscription to move buses
          function start(): void {
            _start.next();
          }

          function stop(): void {
            _stop.next();
          }

          function changeInterval(newInterval: number) {
            delaySub = 0;
            interval = newInterval;
            if (isTest) {
              indexSubject.next(testInterval);
            } else {
              indexSubject.next(interval);
            }
          }

          const busObservable = indexSubject.pipe(
            switchMap(duration => timer(delaySub, duration)),
            map(x => x + pathIndex + 1),
            takeUntil(_stop),
            repeatWhen(() => _start)
          );

          let prevStopPathIndex = prevBusStop ?? 1;
          let nextStopPathIndex = nextBusStop;

          const initPosition = this.computePercent(
            prevStopPathIndex,
            nextStopPathIndex,
            pathIndex
          );
          busData.drivenPercentage = initPosition;

          const busSubscription = busObservable.subscribe(x => {
            if (x <= currPath.length) {
              const busStopIndex = currStop.stop.findIndex(
                stop => stop.index === x
              );

              /**
               * call timetable update here
               */
              if (busStopIndex > -1) {
                const currTime = moment().format('HH:mm');
                const currBusStop = currStop.stop[busStopIndex];
                const nextBusStop =
                  currStop.stop[busStopIndex + 1] ?? undefined;
                const secondBusStop =
                  currStop.stop?.[busStopIndex + 2] ?? undefined;
                const thirdBusStop =
                  currStop.stop?.[busStopIndex + 3] ?? undefined;

                // update busladder
                this.updateDemoBusLadder(
                  busData,
                  currBusStop?.stopCode,
                  nextBusStop?.stopCode,
                  0
                );

                const stopsEtaData = [nextBusStop, secondBusStop, thirdBusStop];

                const { index } = currBusStop;

                prevStopPathIndex = index ?? 1;
                nextStopPathIndex = nextBusStop?.index;

                console.log(busCodePlate, currDir, 'Bus Stop', {
                  x,
                  busData,
                  currBusStop,
                  nextBusStop,
                  currTime,
                  index,
                });
                const { tripId } = assignedTrip;
                const { stopCode } = currBusStop;

                // update stops
                busData.stops = busData.stops.map((_, index) =>
                  this.updateBusStopEta(stopsEtaData[index], 5 * (index + 1))
                );

                this.demoBusStopTimingService.timetableArrival({
                  routeId: route,
                  direction: currDir,
                  tripId,
                  stopId: stopCode,
                  obsArrTime: currTime,
                  busRegNo: busCodePlate,
                });

                // update interval
                if (nextBusStop) {
                  pathIndex = index;
                  const newInterval = computeTimeInterval(
                    index,
                    nextBusStop.index
                  );
                  // console.log('new interval', newInterval);
                  changeInterval(newInterval);
                }
              }

              if (x < currPath.length) {
                const newCoordinates = currPath[x];
                const [newLat, newLong] = newCoordinates;

                newBus
                  .getGeometry()
                  .setCoordinates(
                    transform(
                      [Number(newLong), Number(newLat)],
                      'EPSG:4326',
                      'EPSG:3857'
                    )
                  );

                const newRotation = getAngle(
                  +longitude,
                  +latitude,
                  +newLong,
                  +newLat
                );

                busData.lat = newLat;
                busData.lon = newLong;
                busData.degrees = newRotation;

                // update bus ladder position

                const newDrivenPercentage = this.computePercent(
                  prevStopPathIndex,
                  nextStopPathIndex,
                  x
                );
                // console.log('zpercent', busCodePlate, {
                //   prevStopPathIndex,
                //   nextStopPathIndex,
                //   x,
                //   newDrivenPercentage,
                // });
                busData.drivenPercentage = newDrivenPercentage;

                // update early / late here
                busData.status = '0';

                const newBusStyle = this.createNewBusStyle(busData);
                newBus.setStyle(newBusStyle);

                latitude = newLat;
                longitude = newLong;
              }
            }

            if (x >= currPath.length) {
              pathIndex = 0;
              stop();

              currDir = currDir === 1 ? 2 : 1;
              currPath = routePath.find(dir => dir.value === currDir)?.path;

              currStop = busStops?.find(stop => stop.direction === currDir);
              const prevStop = currStop.stop[0];
              const nextStop = currStop.stop[1];

              currTripIndex = currTripIndex + 1;

              const { flatTimeTable } = blockTimeTable;

              console.log(
                'finished trip',
                busCodePlate,
                tripId,
                busData,
                assignedTrip,
                { currTripIndex, currDir },
                flatTimeTable.length,
                flatTimeTable
              );

              // clear all stops ETA
              busData.stops.forEach(stop => {
                stop.stop = '';
                stop.eta = '-';
              });

              // clear bus stop ladder
              // this.updateDemoBusLadder(busData, undefined, undefined, 0);

              busData.status = '2';
              const newBusStyle = this.createNewBusStyle(busData);
              newBus.setStyle(newBusStyle);

              if (currTripIndex < flatTimeTable.length) {
                assignedTrip = flatTimeTable[currTripIndex];
                const { blockId, tripId } = assignedTrip;
                busData.trip = tripId;
                busData.tripId = tripId;
                busData.blockId = blockId;
                busData.status = '2';
                busData.direction = `${currDir}`;

                const currTime = moment().format('HH:mm');
                const nextTime = assignedTrip.stops[0].schArrTime;

                const waitTime = getTimeDifference(currTime, nextTime);

                // console.log(
                //   busCodePlate,
                //   busData,
                //   tripId,
                //   assignedTrip,
                //   currDir,
                //   { prevStop, nextStop },
                //   `Next Trip (${nextTime}) in: `,
                //   waitTime / 1000 / 60,
                //   'm'
                // );

                this.updateDemoBusLadder(
                  busData,
                  prevStop.stopCode,
                  prevStop.stopCode,
                  0
                );

                setTimeout(
                  () => {
                    const newInterval = computeTimeInterval(0, nextStop.index);
                    changeInterval(newInterval);
                    start();
                    const currBusStop = currStop.stop[0];
                    const { stopCode } = currBusStop;
                    this.demoBusStopTimingService.timetableArrival({
                      routeId: route,
                      direction: currDir,
                      tripId,
                      stopId: stopCode,
                      obsArrTime: currTime,
                      busRegNo: busCodePlate,
                    });

                    // set eta stops
                    const nextBusStop = currStop.stop[1] ?? undefined;
                    const secondBusStop = currStop.stop?.[2] ?? undefined;
                    const thirdBusStop = currStop.stop?.[3] ?? undefined;

                    const stopsEtaData = [
                      nextBusStop,
                      secondBusStop,
                      thirdBusStop,
                    ];
                    busData.stops = busData.stops.map((_, index) =>
                      this.updateBusStopEta(
                        stopsEtaData[index],
                        5 * (index + 1)
                      )
                    );

                    // set new busladder
                    this.updateDemoBusLadder(
                      busData,
                      currBusStop.stopCode,
                      nextBusStop.stopCode,
                      0
                    );

                    prevStopPathIndex = 1;
                    nextStopPathIndex = nextBusStop.index;
                  },
                  isTest ? 2000 : waitTime
                );
              } else {
                console.log('finished all trips', busCodePlate);
              }
            }
          });

          this.busListSubscription.push(busSubscription);
          this.busSubjects.push(_start);
          this.busSubjects.push(_stop);

          this.busList.push({ data: busData, busFeature: newBus });
        } else {
          const currBus = this.busList.find(
            busItem => busItem?.data?.busCode === busCodePlate
          );
          const { busFeature } = currBus;
          this.busLayer.getSource().addFeature(busFeature);
        }
      }
    } else {
      // add each bus to bus layer
      this.busRouteFeatureList[currRoute].forEach(bus => {
        this.busLayer.getSource().addFeature(bus);
      });
    }

    // console.log('buses', this.busList, this.busLayer.getSource().getFeatures());
    if (!startup) {
      this.busLayer.setVisible(true);
    }
  }

  computePercent(fromValue: number, toValue: number, currValue: number) {
    if (
      fromValue === undefined ||
      toValue === undefined ||
      currValue === undefined
    )
      return 0;
    const percentValue =
      ((currValue - fromValue) / (toValue - fromValue)) * 100;
    return percentValue < 0 ? 100 : percentValue;
  }

  updateDemoBusLadder(busData, prevStopCode, nextStopCode, percent) {
    // console.log('update bus ladder', {
    //   busData,
    //   prevStopCode,
    //   nextStopCode,
    //   percent,
    // });
    if (!busData) return;
    if (!prevStopCode || !nextStopCode) {
      busData.prevStopId = '';
      busData.nextStopId = '';
      busData.drivenPercentage = 0;
      return;
    }
    busData.prevStopId = prevStopCode ?? '';
    busData.nextStopId = nextStopCode ?? '';
    busData.drivenPercentage = percent ?? 0;
  }

  updateBusStopEta(
    busStopData,
    time = 0,
    timeUnit: DurationInputArg2 = 'millisecond'
  ) {
    if (busStopData) {
      const stop =
        this.stopNames[busStopData.stopCode || busStopData.stopId] ??
        (busStopData.stopCode || busStopData.stopId);
      // console.log('Update ETA: ', busStopData, stop, this.stopNames);
      const eta = moment().add(time, timeUnit).format('HH:mm');
      return { stop, eta };
    }
    return { stop: '', eta: '-' };
  }

  clearBus() {
    this.route = undefined;
    this.routePath = undefined;

    let features = this.busLayer?.getSource().getFeatures();

    if (features) {
      for (let feature of features) {
        this.busLayer.getSource().removeFeature(feature);
      }
    }

    // this.busList = [];

    // if (this.busListSubscription.length > 0) {
    //   this.busListSubscription.forEach(sub => sub?.unsubscribe());
    //   this.busListSubscription = [];
    // }

    // if (this.busSubjects.length > 0) {
    //   this.busSubjects.forEach(subj => subj?.complete());
    //   this.busSubjects = [];
    // }
  }

  addBus(dataBus) {
    let busColor = '';
    var bus2Style = new Style({});
    let caret = '';
    var textColor = '#fff';

    if (dataBus.status === '0') {
      // on-time
      busColor = ColorData.eGreen;
      caret = 'location-green-32.png';
    } else if (dataBus.status === '-1') {
      // early
      busColor = ColorData.eYellow;
      caret = 'location-yellow-32.png';
      textColor = '#000';
    } else if (dataBus.status === '1') {
      // late
      busColor = ColorData.eRed;
      caret = 'location-red-32.png';
    } else if (dataBus.status === '2') {
      // not on trip
      busColor = ColorData.eGray;
      caret = 'location-gray-32.png';
    } else if (dataBus.status === '3') {
      // ?
      busColor = ColorData.eOrange;
      caret = 'location-orange-32.png';
    }

    var bus1 = new OlFeature({
      type: 'icon',
      geometry: new Point(
        transform(
          [parseFloat(dataBus.lon), parseFloat(dataBus.lat)],
          'EPSG:4326',
          'EPSG:3857'
        )
      ),
      name: 'bus-' + dataBus.busCode,
      description: dataBus,
    });

    var bus1Style = new Style({
      image: new Icon({
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        // opacity: 1,
        src: 'assets/images/icon/' + caret,
        scale: 0.5,
        rotation: (parseFloat(dataBus.degrees) * Math.PI) / 180,
        // rotation: 60 * 0.01745329251,  // in rad / 360° = 6.28318531 rad = 2PI rad
        rotateWithView: true,
      }),

      text: new Text({
        offsetY: -24,
        text: dataBus.busCode,
        fill: new Fill({
          color: textColor,
        }),
        textAlign: 'center',
        justify: 'center',
        textBaseline: 'middle',
        padding: [5, 5, 5, 5],
        font: 'bold 12px Roboto, Helvetica Neue, Verdana, Helvetica, Arial, sans-serif',
      }),
    });

    // rounded background
    const squareText = '\u25A0'.repeat(dataBus.busCode.length);
    const squareBgTextStyle = new Style({
      text: new Text({
        text: squareText,
        offsetY: -26,
        fill: new Fill({
          color: busColor,
        }),
        textAlign: 'center',
        justify: 'center',
        textBaseline: 'middle',
        padding: [5, 5, 5, 5],
        font: 'bold 12px Roboto, Helvetica Neue, Verdana, Helvetica, Arial, sans-serif',
        stroke: new Stroke({
          color: busColor,
          width: 21,
        }),
      }),
    });

    if (dataBus.isSelected) {
      bus2Style = new Style({
        image: new Icon({
          anchor: [0.5, 1.6],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          // opacity: 1,
          src: 'assets/images/icon/map-marker-orange.png',
        }),
        zIndex: 2,
      });
    }
    bus1.setStyle([squareBgTextStyle, bus1Style]);
    return bus1;
  }

  createNewBusStyle(dataBus) {
    let busColor = '';
    let caret = '';
    var textColor = '#fff';

    if (dataBus.status === '0') {
      // on-time
      busColor = ColorData.eGreen;
      // caret = 'caret-green-32.png';
      caret = 'location-green-32.png';
    } else if (dataBus.status === '-1') {
      // early
      busColor = ColorData.eYellow;
      // caret = 'caret-yellow-32.png';
      caret = 'location-yellow-32.png';
      textColor = '#000';
    } else if (dataBus.status === '1') {
      // late
      busColor = ColorData.eRed;
      // caret = 'caret-red-32.png';
      caret = 'location-red-32.png';
    } else if (dataBus.status === '2') {
      // not on trip
      busColor = ColorData.eGray;
      // caret = 'caret-red-32.png';
      caret = 'location-gray-32.png';
    } else if (dataBus.status === '3') {
      // ?
      busColor = ColorData.eOrange;
      caret = 'location-orange-32.png';
    }
    var bus1Style = new Style({
      image: new Icon({
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        // opacity: 1,
        src: 'assets/images/icon/' + caret,
        scale: 0.5,
        rotation: (parseFloat(dataBus.degrees) * Math.PI) / 180,
        // rotation: 60 * 0.01745329251,  // in rad / 360° = 6.28318531 rad = 2PI rad
        rotateWithView: true,
      }),

      text: new Text({
        offsetY: -24,
        text: dataBus.busCode,
        fill: new Fill({
          color: textColor,
        }),
        textAlign: 'center',
        justify: 'center',
        textBaseline: 'middle',
        padding: [5, 5, 5, 5],
        font: 'bold 12px Roboto, Helvetica Neue, Verdana, Helvetica, Arial, sans-serif',
      }),
    });

    // rounded background
    const squareText = '\u25A0'.repeat(dataBus.busCode.length);
    const squareBgTextStyle = new Style({
      text: new Text({
        text: squareText,
        offsetY: -26,
        fill: new Fill({
          color: busColor,
        }),
        textAlign: 'center',
        justify: 'center',
        textBaseline: 'middle',
        padding: [5, 5, 5, 5],
        font: 'bold 12px Roboto, Helvetica Neue, Verdana, Helvetica, Arial, sans-serif',
        stroke: new Stroke({
          color: busColor,
          width: 21,
        }),
      }),
    });

    return [squareBgTextStyle, bus1Style];
  }

  audio;
  playAudio() {
    const audioSource = 'assets/sounds/silence-1h.m4a';
    const audioElement = new Audio(audioSource);
    audioElement.loop = true;
    audioElement.volume = 0.1;
    this.audio = audioElement;
    audioElement
      .play()
      .catch(err => console.error('Failed to play audio', err));
  }

  // bus data getter
  getBusById(busId: string): any | undefined {
    return this.busList.find(b => b?.data?.busCode === busId);
  }

  addBusToPopup(busId: string) {
    const busData = this.getBusById(busId);
    if (busData) {
      this.mockCallService.addPopup(busId, busData);
    }
  }
}
